/**
 * The table's few sounds, synthesised rather than loaded.
 *
 * The production CSP blocks external hosts and we ship no audio files, so
 * every sound here is built from oscillators and a noise buffer. That also
 * keeps them tiny and lets the turn chime be a real chord rather than a beep.
 *
 * Browsers refuse to start audio until the user has interacted with the page,
 * so the context is created lazily on the first gesture and simply stays
 * silent until then.
 */

type Ctor = typeof AudioContext;

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let noise: AudioBuffer | null = null;

function context(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor: Ctor | undefined =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: Ctor }).webkitAudioContext;
  if (!Ctor) return null;

  ctx = new Ctor();
  master = ctx.createGain();
  master.gain.value = 0.25;
  master.connect(ctx.destination);
  return ctx;
}

/**
 * Call from a click or key press. Until this has run at least once the
 * context is suspended and everything below is a no-op.
 */
export function unlockAudio(): void {
  const audio = context();
  if (audio?.state === 'suspended') void audio.resume();
}

function envelope(at: number, attack: number, decay: number, peak: number): GainNode {
  const audio = ctx as AudioContext;
  const gain = audio.createGain();
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(peak, at + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + attack + decay);
  gain.connect(master as GainNode);
  return gain;
}

function tone(freq: number, at: number, duration: number, peak: number, type: OscillatorType): void {
  const audio = ctx as AudioContext;
  const osc = audio.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, at);
  osc.connect(envelope(at, 0.01, duration, peak));
  osc.start(at);
  osc.stop(at + duration + 0.05);
}

/**
 * A short burst of filtered noise — the paper-on-felt part of a card landing.
 *
 * The centre frequency and decay are arguments because the same gesture at a
 * different brightness and length is a card being flicked off a deck rather
 * than one settling onto felt, and a deal is made of the former.
 */
function whoosh(at: number, peak: number, freq = 1800, decay = 0.09, attack = 0.005): void {
  const audio = ctx as AudioContext;
  if (!noise) {
    // A full second, so there is enough of it to take a different slice every
    // time. A fifth of a second was plenty for one card at a time and became
    // the problem the moment fifty of them went off in a row.
    const frames = Math.floor(audio.sampleRate * 1);
    noise = audio.createBuffer(1, frames, audio.sampleRate);
    const data = noise.getChannelData(0);
    for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;
  }
  const source = audio.createBufferSource();
  source.buffer = noise;

  const filter = audio.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(freq, at);
  // Broad. A narrow band on noise starts to whistle, and paper does not.
  filter.Q.value = 0.5;

  source.connect(filter);
  filter.connect(envelope(at, attack, decay, peak));
  /*
   * Start somewhere new in the buffer each time. Playing the same slice of
   * noise repeatedly is playing the same waveform repeatedly, which the ear
   * hears as a machine rather than as fifty different cards.
   */
  source.start(at, Math.random() * 0.9);
  source.stop(at + 0.2);
}

/**
 * Whether a sound can actually be scheduled right now.
 *
 * The `resume()` here is a best effort and does not help the current call:
 * it resolves asynchronously, so the state read on the next line is still
 * `suspended` and this returns false. That is deliberate rather than a race
 * to be tightened — scheduling into a suspended context is worse than
 * silence, because its clock is stopped and everything queued would fire in a
 * heap the moment it resumes. The context is woken on the first interaction
 * instead (see `App.tsx`), so by the time anything wants to make a noise this
 * is already true.
 */
function ready(): boolean {
  const audio = context();
  if (!audio) return false;
  if (audio.state === 'suspended') void audio.resume();
  return audio.state === 'running';
}

/** A card meeting the discard pile. Deliberately dry and quiet. */
export function playCardSound(): void {
  if (!ready()) return;
  const at = (ctx as AudioContext).currentTime;
  whoosh(at, 0.22);
  tone(140, at, 0.05, 0.06, 'triangle');
}

/** Cards coming to you — the same gesture, pitched down and doubled. */
export function pickUpSound(count = 1): void {
  if (!ready()) return;
  const at = (ctx as AudioContext).currentTime;
  for (let i = 0; i < Math.min(count, 3); i++) whoosh(at + i * 0.07, 0.16);
}

/**
 * A row of cards being dealt out — the brrrr of a deck being run through.
 *
 * Not one long noise but one flick per card, which is what the sound actually
 * is: dealing is fast enough that the individual cards fuse. Building it that
 * way means it needs no separate tuning to the animation it accompanies — the
 * same count and the same spacing produce a short *ffft* for a ten-card row
 * and a proper brrrr for the fifty-four of an opening deal, in sync by
 * construction.
 *
 * Every flick is scheduled up front against the audio clock rather than fired
 * from a timer. Fifty-four `setTimeout`s during a fifty-four card animation
 * would be at the mercy of the same main thread that is doing the animating,
 * and the one thing the ear catches instantly is a deal that stutters.
 *
 * @param count   how many cards
 * @param stagger seconds between them, matching the animation's own
 * @param delay   seconds before the first, for a run that follows a deal
 */
export function dealRiffle(count: number, stagger: number, delay = 0): void {
  if (!ready() || count <= 0) return;

  /*
   * Tightly spaced flicks overlap — at 16ms apart with a 50ms tail, six are
   * sounding at once — and noise bursts sum. So the closer they are, the
   * quieter each has to be, or an opening deal arrives as a hiss.
   */
  const base = Math.min(0.16, Math.max(0.05, 0.045 + stagger * 2.2));

  /*
   * Intervals are accumulated, each one drawn fresh, rather than jittered
   * around a fixed grid.
   *
   * This is the whole difference between a flurry of cards and a buzzer, and
   * it is not about the amount of randomness but its shape. Fifty flicks on an
   * exact 16ms grid repeat at 62Hz, and a periodic train of identical clicks
   * is not heard as fifty events at all — it is heard as one low pitch, which
   * is precisely how an old PC speaker made its notes. Jittering each flick
   * around its slot does not help much either: the grid is still there
   * underneath and the ear still finds it. Letting each gap be its own length
   * means there is no period to lock onto.
   *
   * The gaps average out to `stagger`, so the run still tracks the animation
   * over its whole length even though no single card lands on the beat.
   */
  let when = (ctx as AudioContext).currentTime + delay;
  for (let i = 0; i < count; i++) {
    // Everything varies per card, because every real card does: how hard it
    // was thrown, how much of it caught the next, how long it hissed.
    whoosh(
      when,
      base * (0.65 + Math.random() * 0.7),
      1500 + Math.random() * 2200,
      0.03 + Math.random() * 0.035,
      // A fast attack is the tick of the card leaving the deck. It is most of
      // what makes it sound struck rather than blown.
      0.0015 + Math.random() * 0.002,
    );
    when += stagger * (0.55 + Math.random() * 0.9);
  }
}

/**
 * Your turn. A rising major third, which reads as a prompt rather than an
 * alarm — this can fire while someone is looking at another tab.
 */
export function turnChime(): void {
  if (!ready()) return;
  const at = (ctx as AudioContext).currentTime;
  tone(587.33, at, 0.18, 0.16, 'sine'); // D5
  tone(880.0, at + 0.11, 0.26, 0.13, 'sine'); // A5
}

/** Someone went out. Three notes up, brief enough not to delay the scoreboard. */
export function fanfare(): void {
  if (!ready()) return;
  const at = (ctx as AudioContext).currentTime;
  [523.25, 659.25, 783.99].forEach((f, i) => tone(f, at + i * 0.1, 0.3, 0.14, 'triangle'));
}
